import { AionCta } from "@/components/site/AionCta";
import { OG_ABOUT } from "@/lib/og-images";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Instagram } from "lucide-react";
import { PageBreadcrumb } from "@/components/site/PageBreadcrumb";
import { RelatedKeywords } from "@/components/site/RelatedKeywords";
import { RelatedPages } from "@/components/site/RelatedPages";

const TITLE = "Raj Shamani Instagram & Social Media — Official Accounts";
const DESC =
  "Every official Raj Shamani account: Instagram @rajshamani, YouTube, LinkedIn, X and the Figuring Out podcast channels — plus how to spot fake profiles.";
const URL = "https://rajshamani.life/raj-shamani-instagram";
const OG = OG_ABOUT;

const accounts = [
  {
    name: "Instagram",
    handle: "@rajshamani",
    href: "https://www.instagram.com/rajshamani",
    what: "Daily short-form business content, podcast clips and behind-the-scenes.",
  },
  {
    name: "YouTube",
    handle: "@rajshamani",
    href: "https://www.youtube.com/@rajshamani",
    what: "Full Figuring Out podcast episodes and long-form interviews.",
  },
  {
    name: "LinkedIn",
    handle: "Raj Shamani",
    href: "https://www.linkedin.com/in/rajshamani",
    what: "Business commentary, venture updates and hiring posts.",
  },
  {
    name: "X (Twitter)",
    handle: "@rajshamani",
    href: "https://twitter.com/rajshamani",
    what: "Short takes on startups, markets and the Indian economy.",
  },
];

const faq = [
  {
    q: "What is Raj Shamani's Instagram ID?",
    a: "Raj Shamani's official Instagram handle is @rajshamani. It is the verified account he posts business content and podcast clips on.",
  },
  {
    q: "Is Raj Shamani's Instagram verified?",
    a: "Yes — the official @rajshamani account carries Instagram's verification badge. Accounts with extra characters, underscores or numbers in the handle are not his.",
  },
  {
    q: "What is Raj Shamani's YouTube channel?",
    a: "The Figuring Out podcast is published on the @rajshamani YouTube channel, where full episodes with founders, investors, athletes and actors are released weekly.",
  },
  {
    q: "How many followers does Raj Shamani have?",
    a: "Follower counts change constantly across Instagram, YouTube, LinkedIn and X. The live numbers are visible on each profile linked on this page rather than quoted here, so nothing on this site goes stale.",
  },
  {
    q: "How do I contact Raj Shamani instead of DMing him?",
    a: "Direct messages are rarely read. Business, partnership, speaking and media enquiries should go to partnerships@figuringout.co or through the contact form on this site.",
  },
  {
    q: "How can I spot a fake Raj Shamani account?",
    a: "Fake accounts typically promise investment returns, paid mentorship or crypto tips and use handles that copy @rajshamani with added dots, underscores or digits. Raj does not run paid investment groups or DM people with offers.",
  },
];

export const Route = createFileRoute("/raj-shamani-instagram")({
  head: () => ({
    meta: [
      { title: TITLE, key: "title" },
      { name: "description", content: DESC, key: "description" },
      { property: "og:title", content: TITLE, key: "og:title" },
      { property: "og:description", content: DESC, key: "og:description" },
      { property: "og:url", content: URL, key: "og:url" },
      { property: "og:type", content: "article", key: "og:type" },
      { property: "og:image", content: OG, key: "og:image" },
      { property: "og:image:alt", content: "Raj Shamani official social media accounts", key: "og:image:alt" },
      { name: "twitter:card", content: "summary_large_image", key: "twitter:card" },
      { name: "twitter:title", content: TITLE, key: "twitter:title" },
      { name: "twitter:description", content: DESC, key: "twitter:description" },
      { name: "twitter:image", content: OG, key: "twitter:image" },
      { property: "og:locale", content: "en_IN", key: "og:locale" },
      { name: "twitter:site", content: "@rajshamani", key: "twitter:site" },
      { name: "twitter:creator", content: "@rajshamani", key: "twitter:creator" },
      { name: "geo.region", content: "IN", key: "geo.region" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "@id": `${URL}#webpage`,
          name: TITLE,
          headline: TITLE,
          description: DESC,
          url: URL,
          inLanguage: "en",
          isPartOf: { "@type": "WebSite", "@id": "https://rajshamani.life/#website" },
          breadcrumb: { "@id": `${URL}#breadcrumb` },
          publisher: { "@type": "Organization", "@id": "https://rajshamani.life/#organization" },
          primaryImageOfPage: { "@type": "ImageObject", url: OG },
          image: OG,
          about: {
            "@type": "Person",
            "@id": "https://rajshamani.life/about#person",
            name: "Raj Shamani",
            url: "https://rajshamani.life/about",
            sameAs: accounts.map((a) => a.href),
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": `${URL}#faq`,
          mainEntity: faq.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: SocialPage,
});

function SocialPage() {
  return (
    <div>
      <PageBreadcrumb current="Raj Shamani Instagram" />

      <section className="bg-noir border-b border-border/50">
        <div className="mx-auto max-w-5xl px-6 py-24">
          <div className="text-xs tracking-widest uppercase text-gold mb-4 inline-flex items-center gap-2">
            <Instagram size={14} /> Official accounts
          </div>
          <h1 className="text-4xl md:text-6xl font-bold leading-tight">
            Raj Shamani <span className="text-gradient-gold">Instagram</span> &amp; social media
          </h1>
          <p className="mt-8 text-lg text-muted-foreground max-w-3xl leading-relaxed">
            Raj Shamani&apos;s official Instagram handle is{" "}
            <strong className="text-foreground">@rajshamani</strong>. The same handle is used on
            YouTube, X and LinkedIn, where the Figuring Out podcast is published. Everything below
            links to the verified account — anything else is not his.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 py-20">
        <h2 className="text-3xl md:text-4xl font-bold mb-8">Every official account</h2>
        <div className="grid sm:grid-cols-2 gap-6">
          {accounts.map((a) => (
            <a
              key={a.name}
              href={a.href}
              target="_blank"
              rel="noopener noreferrer"
              className="block p-6 rounded-xl border border-border bg-card hover:border-gold/40 transition"
            >
              <div className="text-xs uppercase tracking-widest text-gold mb-2">{a.name}</div>
              <div className="font-bold mb-2">{a.handle}</div>
              <p className="text-sm text-muted-foreground">{a.what}</p>
            </a>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 pb-20">
        <h2 className="text-3xl md:text-4xl font-bold mb-6">Avoiding fake accounts</h2>
        <p className="text-muted-foreground max-w-3xl leading-relaxed">
          Impersonation accounts are common. They copy the profile photo, use a near-identical
          handle and DM people about investment groups, paid mentorship or crypto. Raj Shamani does
          not run any of those. For genuine business, partnership or speaking enquiries, use the{" "}
          <Link to="/contact" className="text-gold underline underline-offset-4">
            official contact page
          </Link>{" "}
          rather than a DM.
        </p>
      </section>

      <section className="mx-auto max-w-5xl px-6 pb-20">
        <h2 className="text-3xl md:text-4xl font-bold mb-8">Frequently asked questions</h2>
        <div className="space-y-6">
          {faq.map((f) => (
            <div key={f.q} className="rounded-xl border border-border bg-card p-6">
              <h3 className="font-bold mb-2">{f.q}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
        <p className="mt-10 text-muted-foreground">
          Prefer the long-form work? Start with{" "}
          <Link to="/raj-shamani-podcast" className="text-gold underline underline-offset-4">
            the Figuring Out podcast
          </Link>
          .
        </p>
      </section>

      <AionCta />
      <RelatedKeywords currentPath="/raj-shamani-instagram" />
      <RelatedPages pages={["aion", "about", "faq", "ventures", "book", "podcast"]} />
    </div>
  );
}
